import { types, applySnapshot } from "mobx-state-tree"
import { Transport } from "modules/common/models/transport"
import { Notificator } from "modules/common/models/notificator"
import { apiUrls } from "modules/common/services/communication/urls"
import { flow } from "modules/common/models/flow"
import moment from "moment"

/** Даты приходят с API в формате ДД.ММ.ГГГГ */
const parseDate = (value: string) => moment(value, ["DD.MM.YYYY", "DD.MM.YYYY HH:mm:ss", moment.ISO_8601], true)

export const CompensationAdditionalSum = types
  .model({
    guid: types.maybeNull(types.string),
    name: types.optional(types.maybeNull(types.string), ""),
    sum: types.number,
  })
  .named("CompensationAdditionalSum")

/** Запись истории условий оплаты: с какого месяца действуют оклад, премия, вычет и доплаты */
export const CompensationRecord = types
  .model({
    since: types.string,
    salary: types.number,
    bonus: types.number,
    decrease: types.number,
    additionalSums: types.array(CompensationAdditionalSum),
    source: types.maybeNull(types.string),
    comment: types.maybeNull(types.string),
    author: types.maybeNull(types.string),
    created: types.maybeNull(types.string),
  })
  .views((self) => ({
    get month() {
      return parseDate(self.since)
    },

    get additionalTotal() {
      return self.additionalSums.reduce((acc, s) => acc + s.sum, 0)
    },
  }))
  .named("CompensationRecord")

export const CompensationSourceLabels: Record<string, string> = {
  hire: "Приём на работу",
  position: "Перевод на должность",
  schedule: "Штатное расписание",
  manual: "Изменение условий",
  restore: "Возврат после исправления месяца",
  migration: "Из штатного расписания",
}

export interface CompensationChange {
  year: number
  month: number
  salary: number
  bonus: number
  onlyThisMonth: boolean
  comment: string
}

export const CompensationHistory = types
  .compose(
    Transport,
    Notificator,
    types.model({
      id: types.string,
      rows: types.array(CompensationRecord),
      loading: types.boolean,
    })
  )
  .actions((self) => {
    const apply = (rows: any[]) => {
      const sorted = [...(rows ?? [])].sort((a, b) => parseDate(b.since).valueOf() - parseDate(a.since).valueOf())
      applySnapshot(self.rows, sorted)
    }

    return {
      load: flow(function* (id: string) {
        self.id = id
        self.loading = true
        try {
          const rows: any[] = yield self.transport.get<any>(apiUrls.schedule.compensation(id))
          apply(rows)
          return true
        } catch (er) {
          self.notify.error(er)
          return false
        } finally {
          self.loading = false
        }
      }),

      save: flow(function* (change: CompensationChange) {
        self.loading = true
        try {
          const rows: any[] = yield self.transport.post<any>(apiUrls.schedule.compensation(self.id), change)
          apply(rows)
          self.notify.success("Условия оплаты сохранены и применены к штатному расписанию")
          return true
        } catch (er) {
          self.notify.error(er)
          return false
        } finally {
          self.loading = false
        }
      }),

      remove: flow(function* (since: string) {
        const month = parseDate(since)
        self.loading = true
        try {
          const rows: any[] = yield self.transport.delete<any>(apiUrls.schedule.compensation(self.id), {
            params: { year: month.year(), month: month.month() + 1 },
          })
          apply(rows)
          return true
        } catch (er) {
          self.notify.error(er)
          return false
        } finally {
          self.loading = false
        }
      }),
    }
  })
  .views((self) => ({
    /** Условия, действующие в месяце (для подстановки в форму) */
    resolve(year: number, month: number) {
      const start = moment([year, month - 1, 1])
      return self.rows.find((r) => !r.month.isAfter(start, "month")) ?? null
    },
  }))
  .named("CompensationHistory")

export type CompensationHistoryType = typeof CompensationHistory.Type
export type CompensationRecordType = typeof CompensationRecord.Type

export const initialState = (): typeof CompensationHistory.SnapshotType => ({
  id: "",
  rows: [],
  loading: false,
})
